import React, { useCallback, useEffect, useState } from 'react';
import { apiBase, authHeaders, resolveOrgId } from '../utils/authHeaders';

const PAGE = 50;

const LABELS = {
  login_success: 'Signed in',
  login_failure: 'Sign-in failed',
  logout: 'Signed out',
  unlock: 'Unlocked the screen',
  unlock_failure: 'Unlock failed',
};

const FILTERS = [
  ['all', 'Everything'],
  ['problems', 'Failures only'],
];

export const isProblem = (event) => /failure/.test(event || '');
export const eventLabel = (event) => LABELS[event] || String(event || '').replace(/_/g, ' ');

/** "12 Nov 2026, 09:41" in the reader's own zone; the raw text when it is not a date. */
export function whenText(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || '');
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

async function getEvents(before) {
  const qs = new URLSearchParams({ limit: String(PAGE), ...(before ? { before } : {}) });
  const res = await fetch(`${apiBase()}/api/platform/login-events?${qs}`, {
    headers: authHeaders({ orgId: resolveOrgId(), json: true }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Could not load sign-ins (${res.status})`);
  return data;
}

/**
 * Who signed in, failed to, or signed out of this organisation, newest first (backend/session_audit.py). For owners,
 * admins and managers; the server refuses everyone else. Failures are marked so a guessed password stands out.
 */
export default function LoginEvents() {
  const [events, setEvents] = useState([]);
  const [next, setNext] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (before) => {
    setBusy(true); setError(null);
    try {
      const data = await getEvents(before);
      setEvents((prev) => (before ? [...prev, ...(data.events || [])] : (data.events || [])));
      setNext(data.next_before || null);
    } catch (e) { setError(e.message); } finally { setBusy(false); setLoaded(true); }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const shown = filter === 'problems' ? events.filter((e) => isProblem(e.event)) : events;
  return (
    <section className="bg-white rounded-2xl shadow-sm p-6 space-y-3" aria-labelledby="login-events-heading" data-testid="login-events">
      <h2 id="login-events-heading" className="font-display text-lg text-ink">Recent sign-ins</h2>
      <div role="group" aria-label="Show" className="flex gap-2">
        {FILTERS.map(([id, label]) => (
          <button key={id} type="button" aria-pressed={filter === id} data-testid={`login-events-filter-${id}`}
            onClick={() => setFilter(id)}
            className={`min-h-[44px] px-4 rounded-full text-sm border ${filter === id ? 'bg-primary-500 text-surface border-primary-500' : 'bg-surface text-ink-muted border-surface-border'}`}>
            {label}
          </button>
        ))}
      </div>
      {error && <p role="alert" className="text-sm text-danger" data-testid="login-events-error">{error}</p>}
      {loaded && !error && shown.length === 0 && (
        <p className="text-sm text-ink-muted" data-testid="login-events-empty">
          {filter === 'problems' ? 'No failed sign-ins in what is loaded.' : 'No sign-ins recorded yet.'}
        </p>
      )}
      {shown.length > 0 && (
        <ul className="divide-y divide-surface-border" data-testid="login-events-list">
          {shown.map((e) => (
            <li key={e.id} data-testid={`login-event-${e.id}`} className="py-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span className={isProblem(e.event) ? 'font-medium text-danger' : 'font-medium text-ink'}>{eventLabel(e.event)}</span>
              <span className="text-ink">{e.email}</span>
              <span className="text-ink-muted">{whenText(e.at)}</span>
              {e.ip && <span className="text-ink-subtle">{e.ip}</span>}
            </li>
          ))}
        </ul>
      )}
      {next && (
        <button type="button" disabled={busy} onClick={() => load(next)} data-testid="login-events-more"
          className="min-h-[44px] px-4 rounded-xl border border-surface-border text-ink hover:bg-surface-sunken">
          {busy ? 'Loading…' : 'Show older'}
        </button>
      )}
    </section>
  );
}
